<template>
  <v-app-bar
    class="app-header !shadow-[0_1px_2px_rgba(16,42,74,0.06),0_6px_16px_-8px_rgba(16,42,74,0.18)]"
  >
    <div
      class="flex items-center justify-between w-full px-3 sm:px-6 lg:px-0 lg:w-[calc(100%-320px)] lg:mx-auto gap-3 sm:gap-[24px]"
    >
      <v-app-bar-nav-icon
        v-if="isShowSideMenu"
        variant="text"
        @click.stop="emit('toggleMenu')"
      ></v-app-bar-nav-icon>
      <div class="flex items-center justify-center gap-[48px] w-full">
        <div class="flex justify-between w-full gap-3 sm:gap-[20px]">
          <div
            class="flex items-center justify-between w-full gap-2 sm:gap-4 md:gap-[30px]"
          >
            <div class="col-span-1">
              <NuxtLink
                :to="`${isFromRevampPath ? '/revamp' : ''}/portal-pembelajaran`"
                class="block w-[72px] sm:w-[104px] lg:w-[112px] shrink-0 transition-opacity hover:opacity-80"
                aria-label="Portal Pembelajaran"
              >
                <img
                  src="/images/lan-ri-logo.png"
                  alt="LAN RI"
                  class="w-full h-auto object-contain"
                />
              </NuxtLink>
            </div>
            <div class="hidden lg:flex gap-6 xl:gap-8 shrink-0">
              <NuxtLink
                v-for="nav in navigationItems"
                :key="nav.path"
                :to="nav.path"
                class="inline-block text-center text-[14px] font-semibold tracking-[0.01em]"
              >
                {{ nav.title }}
              </NuxtLink>
            </div>
            <TextField
              v-show="$vuetify.display.mdAndUp"
              placeholder="Mau Belajar Apa Hari ini?"
              :prefixIcon="IconSearch"
              class="flex-1 max-w-[780px]"
            />
          </div>
          <div class="flex items-center gap-1.5 sm:gap-3 md:gap-4 shrink-0">
            <template v-if="isAuthorized">
              <IconBell :size="24" class="hidden sm:block" />
              <Avatar
                v-if="
                  useUserDataStore().errorGetProfileUser ||
                  !profile?.fileName ||
                  /swajar_old/.test(profile?.fileName)
                "
                :size="$vuetify.display.smAndUp ? 40 : 32"
                :initials="dataPegawai?.nama || ''"
              />
              <Avatar v-else :src="avatar" :size="$vuetify.display.smAndUp ? 40 : 32" alt="Profile" />
              <div ref="userMenuRef" class="relative mr-1 sm:mr-[12px]">
                <button
                  type="button"
                  class="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border-0 bg-transparent !text-inherit transition-colors hover:!bg-[#F3F4F6]"
                  aria-label="Menu pengguna"
                  aria-haspopup="menu"
                  :aria-expanded="isUserMenuOpen"
                  @click="isUserMenuOpen = !isUserMenuOpen"
                >
                  <IconChevronUp v-if="isUserMenuOpen" :size="20" />
                  <IconChevronDown v-else :size="20" />
                </button>
                <Transition
                  enter-active-class="transition duration-150 ease-out"
                  enter-from-class="opacity-0 -translate-y-1"
                  leave-active-class="transition duration-100 ease-in"
                  leave-to-class="opacity-0 -translate-y-1"
                >
                  <div
                    v-if="isUserMenuOpen"
                    role="menu"
                    class="absolute right-0 top-full z-[1100] mt-3.5 w-[264px] rounded-lg border border-[#E2E8F0] bg-white p-2 shadow-[0_1px_2px_rgba(15,42,74,0.05),0_16px_32px_-12px_rgba(15,42,74,0.22)]"
                  >
                    <template v-for="item in userMenuItems" :key="item.action">
                      <div
                        v-if="item.action === 'logout'"
                        class="mx-3 my-2 h-px bg-[#E2E8F0]"
                      ></div>
                      <button
                        type="button"
                        role="menuitem"
                        class="flex w-full cursor-pointer items-center gap-3 border-0 bg-transparent px-3 py-2 text-left text-[14px] font-medium transition-colors"
                        :class="
                          item.action === 'logout'
                            ? '!text-[#DC2626] hover:!bg-[#F3F4F6]'
                            : 'text-[#0F2A4A] hover:!bg-[#F3F4F6]'
                        "
                        @click="onMenuItemClick(item)"
                      >
                        <span
                          class="flex h-[34px] w-[34px] shrink-0 items-center justify-center transition-colors"
                          :class="
                            item.action === 'logout'
                              ? 'text-[#DC2626]'
                              : 'text-[#475569]'
                          "
                        >
                          <component :is="item.icon" :size="18" />
                        </span>
                        {{ item.title }}
                      </button>
                    </template>
                  </div>
                </Transition>
              </div>
            </template>
            <NuxtLink
              v-else
              to="/login"
              class="cursor-pointer rounded-[8px] sm:rounded-[12px] sm:px-[20px] sm:py-[12px] px-[16px] py-[8px] bg-white !text-[#2663A3] hover:!bg-[#2663A3] hover:!text-white transition-all !border !border-[#2663A3] font-bold text-[12px] sm:text-[14px]"
            >
              Masuk
            </NuxtLink>
          </div>
        </div>
      </div>
    </div>
  </v-app-bar>
</template>
<script setup>
import { Avatar, TextField } from "@idds/vue";
import {
  IconChevronDown,
  IconUser,
  IconDoorExit,
  IconChevronUp,
  IconSearch,
  IconBell,
  IconBook,
} from "@tabler/icons-vue";

const { isFromRevampPath } = defineProps({
  isShowSideMenu: {
    type: Boolean,
    default: false,
  },
  isFromRevampPath: {
    type: Boolean,
    default: false,
  },
});
const emit = defineEmits(["toggleMenu"]);

const isUserMenuOpen = ref(false);
const userMenuRef = ref(null);
const avatar = ref("");
const isAuthorized = computed(() => {
  if (typeof window === "undefined") return false;
  const lsToken = localStorage.getItem("tokenBelajarWajib");
  return !!lsToken && lsToken !== "undefined";
});
const navigationItems = [
  {
    title: "Jelajahi Portal",
    path: `${isFromRevampPath ? "/revamp" : ""}/portal-pembelajaran`,
  },
  {
    title: "Pembelajaran Saya",
    path: `${isFromRevampPath ? "/revamp" : ""}/portal-pembelajaran`,
  },
];
const userMenuItems = [
  {
    title: "Portal Pembelajaran",
    action: "learning",
    path: `${isFromRevampPath ? "/revamp" : ""}/portal-pembelajaran`,
    icon: IconBook,
  },
  {
    title: "Profil Saya",
    action: "profile",
    path: `${isFromRevampPath ? "/revamp" : ""}/profil-saya`,
    icon: IconUser,
  },
  { title: "Keluar", action: "logout", icon: IconDoorExit },
];

const profile = computed(() => {
  return useUserDataStore().profile || {};
});

const updateAvatar = () => {
  const { fileName } = profile.value;
  const baseUrl = useRuntimeConfig().public.apiBelajarWajib;
  avatar.value = `${baseUrl}/image/file/image/${fileName}`;
};

watch(profile, (newVal) => {
  if (isAuthorized.value && Object.keys(newVal).length > 0) {
    updateAvatar();
  }
});

watch(
  isAuthorized,
  (authorized) => {
    if (!authorized) {
      avatar.value = "https://placehold.co/200x200/eeeeee/eeeeee";
      isUserMenuOpen.value = false;
      return;
    }
    updateAvatar();
  },
  { immediate: true },
);

const handleMenuAction = ({ action, path }) => {
  if (action === "logout") {
    useAuthStore().clearTokens();
    navigateTo(`${isFromRevampPath ? "/revamp" : ""}/login`);
  } else if (path) {
    navigateTo(path);
  }
};

const onMenuItemClick = (item) => {
  isUserMenuOpen.value = false;
  handleMenuAction(item);
};

const closeOnOutsideClick = (event) => {
  // composedPath() is fixed at dispatch time. event.target can already be
  // detached by then: toggling swaps the chevron icon, and Vue patches the DOM
  // between the button's click handler and this document-level one.
  const inside = event.composedPath().includes(userMenuRef.value);
  if (isUserMenuOpen.value && !inside) {
    isUserMenuOpen.value = false;
  }
};

const closeOnEscape = (event) => {
  if (event.key === "Escape") isUserMenuOpen.value = false;
};

const dataPegawai = computed(() => {
  return useUserDataStore().dataPegawai || {};
});

onBeforeUnmount(() => {
  document.removeEventListener("click", closeOnOutsideClick);
  document.removeEventListener("keydown", closeOnEscape);
});

onMounted(() => {
  document.addEventListener("click", closeOnOutsideClick);
  document.addEventListener("keydown", closeOnEscape);
  if (isAuthorized.value) {
    updateAvatar();
  }
});
</script>

<style>
/* v-toolbar__content clips overflow, which hides the absolutely positioned
   dropdown panel that hangs below the bar */
.app-header .v-toolbar__content {
  overflow: visible;
}
</style>
